import type { GraphEdge, GraphNode, NodeKind } from "./buildAccessModel";

export type NodePosition = { x: number; y: number; col: number; row: number };
type ColumnMeta = {
  kind: NodeKind;
  x: number;
  count: number;
  label: string;
};
export type GraphLayout = {
  positions: Map<string, NodePosition>;
  columns: ColumnMeta[];
  width: number;
  height: number;
};

export const COLUMN_ORDER: NodeKind[] = [
  "user",
  "group",
  "space",
  "app",
  "skill",
];
const COLUMN_LABEL: Record<NodeKind, string> = {
  user: "Members",
  group: "Groups",
  app: "Connectors",
  skill: "Skills",
  space: "Spaces",
};

export const NODE_WIDTH = 208;
export const NODE_HEIGHT = 48;
const COL_GAP = 64;
const ROW_GAP = 12;
export const COLUMN_PITCH = NODE_WIDTH + COL_GAP;
export const ROW_PITCH = NODE_HEIGHT + ROW_GAP;

export type ColumnSort = Partial<Record<NodeKind, "asc" | "desc">>;

// Sugiyama would be overkill: RBAC graphs aren't layered DAGs (group membership
// and cross-resource edges create cycles). A few barycenter sweeps cut enough
// crossings to be legible, and stay O(edges).
export function layoutGraph(
  nodes: readonly GraphNode[],
  edges: readonly GraphEdge[],
  columnSort: ColumnSort = {}
): GraphLayout {
  const colIndex = new Map(COLUMN_ORDER.map((kind, i) => [kind, i] as const));
  const buckets: GraphNode[][] = COLUMN_ORDER.map(() => []);
  for (const n of nodes) {
    const ci = colIndex.get(n.kind);
    if (ci !== undefined) buckets[ci]!.push(n);
  }
  // A stable name-asc seed so the barycenter result is deterministic and
  // independent of which columns the user has sorted.
  for (const bucket of buckets) {
    bucket.sort((a, b) => a.name.localeCompare(b.name));
  }

  const adjacency = new Map<string, string[]>();
  const link = (a: string, b: string) => {
    let list = adjacency.get(a);
    if (!list) {
      list = [];
      adjacency.set(a, list);
    }
    list.push(b);
  };
  // Inherited edges are only drawn on focus, so they shouldn't constrain the
  // resting layout.
  for (const e of edges) {
    if (e.type === "inherited") continue;
    link(e.sourceId, e.targetId);
    link(e.targetId, e.sourceId);
  }

  const rowOf = new Map<string, number>();
  const refreshRows = () => {
    for (const bucket of buckets) bucket.forEach((n, i) => rowOf.set(n.id, i));
  };
  refreshRows();

  const barycenter = (id: string): number | null => {
    const neighbors = adjacency.get(id);
    if (!neighbors || neighbors.length === 0) return null;
    let sum = 0;
    let count = 0;
    for (const m of neighbors) {
      const r = rowOf.get(m);
      if (r !== undefined) {
        sum += r;
        count++;
      }
    }
    return count ? sum / count : null;
  };

  for (let iter = 0; iter < 4; iter++) {
    const columnSweep =
      iter % 2 === 0
        ? buckets.map((_, i) => i)
        : buckets.map((_, i) => i).reverse();
    for (const ci of columnSweep) {
      const bucket = buckets[ci]!;
      const weight = new Map<string, number>();
      bucket.forEach((n, i) => weight.set(n.id, barycenter(n.id) ?? i));
      bucket.sort(
        (a, b) =>
          weight.get(a.id)! - weight.get(b.id)! || a.name.localeCompare(b.name)
      );
      refreshRows();
    }
  }

  // Each column's explicit sort is a final override, applied after barycenter
  // so it reorders only that column — the rest keep their resting order.
  for (let ci = 0; ci < buckets.length; ci++) {
    const dir = columnSort[COLUMN_ORDER[ci]!];
    if (!dir) continue;
    buckets[ci]!.sort(
      (a, b) => a.name.localeCompare(b.name) * (dir === "desc" ? -1 : 1)
    );
  }

  const positions = new Map<string, NodePosition>();
  let maxRows = 0;
  buckets.forEach((bucket, ci) => {
    maxRows = Math.max(maxRows, bucket.length);
    bucket.forEach((n, row) => {
      positions.set(n.id, {
        x: ci * COLUMN_PITCH,
        y: row * ROW_PITCH,
        col: ci,
        row,
      });
    });
  });

  const columns: ColumnMeta[] = COLUMN_ORDER.map((kind, ci) => ({
    kind,
    x: ci * COLUMN_PITCH,
    count: buckets[ci]!.length,
    label: COLUMN_LABEL[kind],
  }));

  return {
    positions,
    columns,
    width: (COLUMN_ORDER.length - 1) * COLUMN_PITCH + NODE_WIDTH,
    height: maxRows * ROW_PITCH,
  };
}
